/**
 * DarkLockup.js
 *
 * The horizontal Constellation KG lockup, drawn for a dark background.
 *
 * Inline SVG rather than a file, following the precedent in
 * Header.js (LogoMark, Header.js:15-30) — the app has drawn its mark inline
 * since the header was built, and a second mechanism for the same artwork is
 * not worth the divergence.
 *
 * WHY A NEW VARIANT EXISTS AT ALL
 * -------------------------------
 * Neither shipped lockup reads on a dark page. Both public/logo-ckg-lockup.svg
 * and public/logo-ckg-stacked.svg embed the LIGHT mark (white tile, teal ink)
 * with the title in #243C4C — which measures 1.71:1 against the sky's darkest
 * band and 1.14:1 against its lightest. Effectively invisible, and the white
 * tile would read as a bright block punched into the sky.
 *
 * WHAT THIS IS, GEOMETRICALLY
 * ---------------------------
 * public/logo-ckg-lockup.svg's geometry — the same 320x50 viewBox, the same
 * 44x44 mark at x=0 y=3, the same text baselines at y=24 and y=42 — with the
 * mark's construction INVERTED from public/logo-ckg-light.svg: that file's
 * rect/C/star drawn in ivory #FFFCEF, and its tile left unfilled so the sky
 * shows through. Every path and coordinate is copied from those two files; only
 * the fills change.
 *
 * The existing logo files and Header.js are deliberately untouched.
 *
 * The lockup stays in Helvetica. The rest of the landing page uses Fraunces and
 * IBM Plex Sans, but the mark is brand artwork and STYLE_GUIDE.md:628-636 fixes
 * its typeface — a webfont here would change the logo, not the page.
 */

import React from 'react';

// Ivory, from the brand palette (STYLE_GUIDE.md:594) — the same ink the
// positive mark and the favicon already use on teal. 19.1:1 against the sky's
// darkest band, 12.8:1 against its lightest.
const INK = '#FFFCEF';
// The trademark mark and tagline are lightened from their light-background
// values (#698696 and #5C899D) so they clear AA-large on the sky; both values
// come from the approved reference.
const TM = '#8FA3A7';
const TAGLINE = '#7FAABB';

const DarkLockup = () => (
  <svg
    className="landing-sky-lockup"
    xmlns="http://www.w3.org/2000/svg"
    width="320"
    height="50"
    viewBox="0 0 320 50"
    aria-hidden="true"
    focusable="false"
  >
    {/* The mark: logo-ckg-light.svg's construction, inverted. The rect has no
        fill, so the rotating sky shows through the tile. */}
    <svg x="0" y="3" width="44" height="44" viewBox="0 0 160 160">
      <rect
        x="2.5"
        y="2.5"
        width="155"
        height="155"
        rx="22"
        fill="none"
        stroke={INK}
        strokeWidth="5"
      />
      <text
        x="80"
        y="126"
        fontFamily="Helvetica, Arial, sans-serif"
        fontSize="140"
        fontWeight="400"
        fill={INK}
        textAnchor="middle"
      >
        C
      </text>
      {/* Eight-point star (Noun Project, Ralf Schmitzer), same transform and
          points as the shipped marks. */}
      <g transform="translate(53, 46) scale(0.60)">
        <polygon
          fill={INK}
          points="91.25,50 54.903,47.234 67.887,32.114 52.765,45.096 50,8.75 47.234,45.096 32.113,32.114 45.097,47.234 8.75,50 45.096,52.765 32.114,67.886 47.234,54.903 50,91.25 52.765,54.902 67.886,67.886 54.903,52.765"
        />
      </g>
    </svg>

    <text
      x="58"
      y="24"
      fontFamily="Helvetica, Arial, sans-serif"
      fontSize="22"
      fontWeight="400"
      fill={INK}
    >
      Constellation KG
      <tspan fontSize="9" fill={TM} dy="-8" dx="1">
        &#8482;
      </tspan>
    </text>
    <text
      x="58"
      y="42"
      fontFamily="Helvetica, Arial, sans-serif"
      fontSize="13"
      fontWeight="400"
      fill={TAGLINE}
      className="landing-sky-lockup-tagline"
    >
      Explore your knowledge
    </text>
  </svg>
);

export default DarkLockup;
